/**
 * Pure helpers for the native map adapter (nativeMap.js). Kept free of any
 * Capacitor / Google import so they run under plain `node --test`.
 */

/**
 * Should this screen draw the native Android map instead of the JS map?
 * All of these must hold:
 *  - running inside the installed app (not the website),
 *  - the native plugin is compiled into this APK/AAB,
 *  - the build was made with REACT_APP_NATIVE_MAPS=1 (the release switch:
 *    only set it once the Android Maps key is in android/local.properties).
 * Anything else → the existing JS map, unchanged.
 */
export const shouldUseNativeMap = ({ isNative, pluginAvailable, flag }) =>
  Boolean(isNative && pluginAvailable && String(flag || '').trim() === '1');

/** google.maps.LatLng (lat() functions) or a plain {lat,lng} → plain {lat,lng}. */
export const toLatLngLiteral = (p) => {
  if (!p) return null;
  const lat = typeof p.lat === 'function' ? p.lat() : Number(p.lat);
  const lng = typeof p.lng === 'function' ? p.lng() : Number(p.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
};

/** Smallest box holding every valid point, in the plugin's LatLngBounds shape. */
export const boundsForPoints = (points) => {
  const pts = (points || []).map(toLatLngLiteral).filter(Boolean);
  if (!pts.length) return null;
  let south = pts[0].lat; let north = pts[0].lat;
  let west = pts[0].lng; let east = pts[0].lng;
  pts.forEach(({ lat, lng }) => {
    south = Math.min(south, lat); north = Math.max(north, lat);
    west = Math.min(west, lng); east = Math.max(east, lng);
  });
  return {
    southwest: { lat: south, lng: west },
    northeast: { lat: north, lng: east },
    center: { lat: (south + north) / 2, lng: (west + east) / 2 },
  };
};

/**
 * The plugin's fitBounds takes one padding number (CSS px), the JS map takes
 * per-side padding. Use the largest side so nothing ends up under a sheet.
 */
export const paddingToNumber = (padding, fallback = 64) => {
  if (Number.isFinite(padding)) return padding;
  if (padding && typeof padding === 'object') {
    const sides = ['top', 'bottom', 'left', 'right']
      .map((k) => Number(padding[k]))
      .filter(Number.isFinite);
    if (sides.length) return Math.max(...sides);
  }
  return fallback;
};

/**
 * The plugin's fitBounds padding and line widths are raw device pixels (no
 * density scaling), while our callers think in CSS px. Convert, and round:
 * the plugin reads padding as an integer and silently uses 0 for a decimal.
 */
export const cssToDevicePx = (css, dpr) => {
  const ratio = Number.isFinite(dpr) && dpr > 0 ? dpr : 1;
  return Math.round(Number(css) * ratio);
};

/**
 * fitBounds padding in device px, capped at 40% of the map's smaller side.
 * Android's newLatLngBounds throws if the padding leaves no room, and the
 * plugin calls it outside any error handler, so a too-big padding would
 * crash the app instead of just framing badly.
 */
export const fitPaddingDevicePx = ({ padding, dpr, width, height }) => {
  const want = cssToDevicePx(paddingToNumber(padding), dpr);
  const side = Math.min(Number(width) || 0, Number(height) || 0);
  if (!(side > 0)) return 0;
  const cap = Math.floor(cssToDevicePx(side, dpr) * 0.4);
  return Math.max(0, Math.min(want, cap));
};

/**
 * Margin (device px) kept around the pins when fitting inside the visible
 * part of the map (map padding already hides the sheet and top bar). Without
 * it the fit put the points exactly on the edges, so the 42 px A/B pins stood
 * under the top buttons and on the sheet's edge. `want` covers a pin's height;
 * it shrinks to 30% of the visible area's smaller side, so a short visible
 * strip always keeps room to fit into (Android throws if there is none).
 */
export const fitMarginDevicePx = ({ width, height, dpr, want = 48 }) => {
  const side = Math.min(Number(width) || 0, Number(height) || 0);
  if (!(side > 0)) return 0;
  return cssToDevicePx(Math.min(want, side * 0.3), dpr);
};

/**
 * Per-side map padding (top/bottom/left/right) in device px, for screens with
 * a bottom sheet: the map treats the padded area as hidden, so fits and
 * camera moves land in the part the rider can see. Each axis is capped at
 * 85% of the map so there is always room left to fit into. (60% was too
 * tight: Book's confirm sheet covers ~72% of a phone, and the route was
 * framed partly under it.)
 */
export const mapPaddingDevicePx = ({ padding, dpr, width, height }) => {
  const side = (k) => Math.max(0, Number(padding?.[k]) || 0);
  const axis = (a, b, size) => {
    const cap = Math.max(0, Number(size) || 0) * 0.85;
    const sum = a + b;
    const scale = sum > cap ? (sum ? cap / sum : 0) : 1;
    return [cssToDevicePx(a * scale, dpr), cssToDevicePx(b * scale, dpr)];
  };
  const [top, bottom] = axis(side('top'), side('bottom'), height);
  const [left, right] = axis(side('left'), side('right'), width);
  return { top, bottom, left, right };
};

/**
 * Runs async jobs one after another. A native marker has no "move" call, so a
 * move is remove + add; without a queue two quick location ticks could add
 * two autos, or a remove could land before its add has returned an id.
 */
export const createSerialQueue = () => {
  let tail = Promise.resolve();
  return (job) => {
    const run = tail.then(job, job);
    tail = run.catch(() => {});
    return run;
  };
};

// Native marker icons, in public/map-icons/ (PNG: the native SDK can't draw SVG). The
// plugin reads them from the app's bundled web assets.
export const NATIVE_ICONS = {
  A: { iconUrl: 'map-icons/pin-a.png', iconSize: { width: 30, height: 42 }, iconAnchor: { x: 15, y: 42 } },
  B: { iconUrl: 'map-icons/pin-b.png', iconSize: { width: 30, height: 42 }, iconAnchor: { x: 15, y: 42 } },
  auto: { iconUrl: 'map-icons/auto.png', iconSize: { width: 36, height: 36 }, iconAnchor: { x: 18, y: 18 } },
  // The live-trip auto (AnimateGliderMarker): a heading arrow that turns with
  // the road (flat, so it stays true to north) under an upright auto.
  gliderArrow: { iconUrl: 'map-icons/glider-arrow.png', iconSize: { width: 36, height: 36 }, iconAnchor: { x: 18, y: 18 }, isFlat: true },
  gliderAuto: { iconUrl: 'map-icons/glider-auto.png', iconSize: { width: 36, height: 36 }, iconAnchor: { x: 18, y: 18 } },
  // Saved Home / Work pins on the booking map (same look as the web SVGs).
  savedHome: { iconUrl: 'map-icons/saved-home.png', iconSize: { width: 38, height: 45 }, iconAnchor: { x: 19, y: 45 } },
  savedWork: { iconUrl: 'map-icons/saved-work.png', iconSize: { width: 38, height: 45 }, iconAnchor: { x: 19, y: 45 } },
};

/**
 * The zoom at which `bounds` exactly fills a width x height CSS-px viewport,
 * and the bounds' Web Mercator centre (what a fit would centre on). Same
 * 256 * 2^zoom maths as containerPixelToLatLng. A single point gives zoom
 * Infinity. Lets fitPoints honour a zoom cap in ONE camera move.
 */
export const fitZoomForBounds = ({ bounds, width, height }) => {
  const sw = toLatLngLiteral(bounds?.southwest);
  const ne = toLatLngLiteral(bounds?.northeast);
  if (!sw || !ne || !(width > 0) || !(height > 0)) return null;
  const worldY = (lat) => {
    const sin = Math.sin((lat * Math.PI) / 180);
    return 0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI);
  };
  const dx = Math.abs(ne.lng - sw.lng) / 360;
  const yTop = worldY(ne.lat);
  const yBottom = worldY(sw.lat);
  const dy = Math.abs(yBottom - yTop);
  const zx = dx > 0 ? Math.log2(width / (256 * dx)) : Infinity;
  const zy = dy > 0 ? Math.log2(height / (256 * dy)) : Infinity;
  const n = Math.PI - 2 * Math.PI * ((yTop + yBottom) / 2);
  const lat = (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
  return { zoom: Math.min(zx, zy), center: { lat, lng: (sw.lng + ne.lng) / 2 } };
};

/**
 * The coordinate under a pixel of the map element, for the centre-pin pickers
 * (the native map has no OverlayView projection). Standard Web Mercator at
 * 256 * 2^zoom world px (the native map, like the plugin, treats CSS px as
 * dp), north-up and flat (rotate/tilt are locked in the plugin patch). The
 * camera centre sits in the middle of the PADDED area, so `padding` (CSS px)
 * must be the map padding currently applied.
 */
export const containerPixelToLatLng = ({ center, zoom, width, height, padding = {}, x, y }) => {
  const c = toLatLngLiteral(center);
  if (!c || !Number.isFinite(zoom) || !(width > 0) || !(height > 0)) return null;
  const left = Number(padding.left) || 0;
  const right = Number(padding.right) || 0;
  const top = Number(padding.top) || 0;
  const bottom = Number(padding.bottom) || 0;
  const cx = left + (width - left - right) / 2;
  const cy = top + (height - top - bottom) / 2;
  const scale = 256 * 2 ** zoom;
  const sin = Math.sin((c.lat * Math.PI) / 180);
  const wx = ((c.lng + 180) / 360) * scale + (x - cx);
  const wy = (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * scale + (y - cy);
  const lng = (wx / scale) * 360 - 180;
  const n = Math.PI - (2 * Math.PI * wy) / scale;
  const lat = (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
  return { lat, lng };
};
